import React from 'react';
import { MonthlyDatum } from '../stats';
import { Currency, Money } from 'rpc/model/squareup/connect/v2/common/money';
import { formatConnectMoney } from 'utils/currency';
import { theme } from 'tailwind.config';

type SpendOverTimeChartProps = {
  data: MonthlyDatum[];
  currency: Currency;
};

const CHART_HEIGHT = 160;

const SpendOverTimeChart: React.FC<SpendOverTimeChartProps> = ({
  data,
  currency,
}) => {
  if (data.length === 0) {
    return <p className="paragraph-20 text-grey my-0">No data yet.</p>;
  }

  const max = Math.max(...data.map((datum) => datum.amount), 1);
  // Only label a handful of months so the axis stays legible on mobile.
  const labelEvery = Math.ceil(data.length / 6);
  const tickIndices = data
    .map((_, index) => index)
    .filter((index) => index % labelEvery === 0);

  return (
    <div>
      <div
        className="flex items-end gap-1 w-full"
        style={{ height: CHART_HEIGHT }}
      >
        {data.map((datum) => {
          const heightPct = Math.max((datum.amount / max) * 100, 1);
          const money = formatConnectMoney(
            Money.create({ amount: datum.amount, currency })
          );
          return (
            <div
              key={datum.month}
              className="flex-1 flex items-end h-full"
              title={`${datum.label}: ${money} (${datum.orderCount} ${
                datum.orderCount === 1 ? 'order' : 'orders'
              })`}
            >
              <div
                className="w-full rounded-t"
                style={{
                  backgroundColor: theme.extend.colors.blue,
                  height: `${heightPct}%`,
                }}
              />
            </div>
          );
        })}
      </div>
      {/*
        Absolutely position the sparse tick labels over their bar's center so
        each one can render in full instead of being clipped to a single bar's
        sliver-width column when the timeline spans many months.
      */}
      <div className="relative w-full mt-2 h-4">
        {tickIndices.map((index, order) => {
          const centerPct = ((index + 0.5) / data.length) * 100;
          const transform =
            order === 0
              ? 'translateX(0)'
              : order === tickIndices.length - 1
              ? 'translateX(-100%)'
              : 'translateX(-50%)';
          return (
            <span
              key={data[index].month}
              className="absolute top-0 paragraph-20 text-grey whitespace-nowrap"
              style={{ left: `${centerPct}%`, transform }}
            >
              {data[index].label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default SpendOverTimeChart;
